import _ from 'lodash'
import request from 'request'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { withStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'

import './App.css'
import bitcoin from './bitcoin.svg'
import FeesTable from './FeesTable'
import Footer from './Footer'
import Legend from './Legend'
import TimeSelector from './TimeSelector'

const styles = theme => ({
  app: {
    textAlign: 'center'
  },
  header: {
    backgroundColor: '#222',
    height: '180px',
    padding: '20px',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',

    '& h1': {
      fontSize: '1.5em'
    }
  },
  main: {
    margin: '5rem auto',

    '& > p:first-child': {
      fontSize: 'large'
    }
  },

  price: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& > span:first-child': {
      marginRight: '1rem',
      fontSize: '36px',
      textAlign: 'left',

      '& + span': {
        marginTop: '4px'
      }
    }
  },

  fees: {
    textAlign: 'left',
    borderTop: '1px solid #eee',
    paddingTop: '2.5rem',

    '& > p': {
      paddingBottom: '1em'
    }
  },

  legend: {
    fontSize: '12px'
  },
  tooltipTrigger: {
    borderBottom: '1px dotted #aaa',
    cursor: 'help'
  },

  '@media (max-width: 480px)': {
    price: {
      flexDirection: 'column',
      height: '170px',
      justifyContent: 'space-around',
      marginTop: '2rem'
    }
  }
})

const confirmationTimes = {
  fastestFee: {
    short: '10 minutes',
    long: '10 minutes (fastest)'
  },
  halfHourFee: {
    short: '30 minutes',
    long: '30 minutes'
  },
  hourFee: {
    short: '1 hour',
    long: '1 hour'
  }
}

class App extends Component {
  constructor(props) {
    super(props)

    this.state = {
      hourFee: undefined,
      halfHourFee: undefined,
      fastestFee: undefined,
      btcPrice: undefined,
      confirmation: 'hourFee'
    }

    this.handleTimeSelectorChange = this.handleTimeSelectorChange.bind(this)
  }

  componentDidMount() {
    this.fetch()
  }

  fetch() {
    const now = moment()
    const lastUpdate = localStorage.getItem('lastUpdate')
    const diff = now.diff(lastUpdate, 'seconds')
    console.log(`Last fetch was ${diff} seconds ago.`)

    if (isNaN(diff) || diff > 120) {
      console.log('new fetch!!!!')

      // Fetch all fees and calculate
      request({
        url: 'https://mempool.space/api/v1/fees/recommended',
        json: true
      }, (error, response, body) => {
        if (!error && response.statusCode === 200) {

          console.log('body: ', body)

          if (body.hourFee) {
            const data = {
              hourFee: body.hourFee,
              halfHourFee: body.halfHourFee,
              fastestFee: body.fastestFee
            }
            localStorage.setItem('recommendedBtcFees', JSON.stringify(data))
            localStorage.setItem('lastUpdate', now.format())
            this.setState(_.extend(data, {
              lastUpdate: now.format()
            }))
          } else {
            console.log('Unable to fetch all fees.')
          }
        } else {
          console.log('Unable to fetch the mempool.space API.')
        }
      })
    } else {
      console.log('read storage')

      let recommendedBtcFees = JSON.parse(localStorage.getItem('recommendedBtcFees')) || {}
      this.setState(_.extend(recommendedBtcFees, {
        btcPrice: _.toNumber(localStorage.getItem('btcPrice'))
      }))
    }

    request({
      url: 'https://api.coindesk.com/v1/bpi/currentprice/USD.json',
      json: true
    }, (error, response, body) => {
      if (!error && response.statusCode === 200) {
        localStorage.setItem('btcPrice', body.bpi.USD.rate_float)
        this.setState({
          btcPrice: body.bpi.USD.rate_float
        })
      } else {
        console.log('Unable to fetch BTC price from coindesk.com API.')
      }
    })
  }

  getPrice(satPerByte) {
    if (typeof satPerByte === 'undefined') {
      return '?'
    }
    return `$${_.round(satPerByte * 225 * this.state.btcPrice / 100000000, 2).toFixed(2)}`
  }

  getPriceFromTime(timeSelectorValue) {
    return this.getPrice(this.state[this.state.confirmation])
  }

  handleTimeSelectorChange(key, value) {
    this.setState({[key]: value})
    this.fetch()
  }

  render() {
    const { classes } = this.props

    return (
      <div className={classes.app}>
        <header className={classes.header}>
          <img src={bitcoin} alt="logo" />
          <h1>Bitcoin Fees</h1>
        </header>
        <main className={classes.main}>
          <p>Current fee for a Bitcoin transaction is</p>

          <div className={classes.price}>
            <span>
              {this.getPriceFromTime()}
            </span>

            <Typography>for confirmation in</Typography>

            <TimeSelector
              confirmationTimes={confirmationTimes}
              onChange={this.handleTimeSelectorChange}
              default={this.state.confirmation}
            />
          </div>
        </main>

        <div className="container">
          <div className={classes.fees}>
            <Typography>
              Open the settings of your wallet and change the custom transaction fee to the one calculated here. Use the table below for reference. Since we are sending BTC, the actual fee is also in BTC (or parts of BTC) and not in USD or other fiat money.
            </Typography>
            <Typography>
              For a confirmation in <b>~{confirmationTimes[this.state.confirmation].short}</b> you have to set a custom fee of <b>{this.state[this.state.confirmation] + ' '}
              <Tooltip title="100,000,000 satoshi = 1 BTC" placement="top">
                <span className={classes.tooltipTrigger}>sat</span>
              </Tooltip>/B</b>.
            </Typography>
            <Typography>
              When the transaction is processed, these <b>{this.state[this.state.confirmation]} satoshi</b> will automatically be multiplied by the total bytes that are needed for the transaction (average is 225 bytes) and only then you'll get the total fee which will be automatically deducted from the sent amount.
            </Typography>
            <FeesTable fee={this.state[this.state.confirmation]} />

            {/* <div className={classes.legend}>
              <p>Legend</p>
              1 Satoshi	= 0.00000001 BTC<br/>
              10 Satoshi = 0.0000001 BTC<br/>
              100 Satoshi	= 0.000001 BTC = 1 Bit / μBTC<br/>
              1,000 Satoshi	= 0.00001 BTC<br/>
              10,000 Satoshi = 0.0001 BTC<br/>
              100,000 Satoshi	= 0.001 BTC = 1 mBTC<br/>
              1,000,000 Satoshi	= 0.01 BTC = 1 cBTC<br/>
              10,000,000 Satoshi = 0.1 BTC<br/>
              100,000,000 Satoshi	= 1 BTC
            </div> */}
          </div>
        </div>

        <Legend
          btcPrice={this.state.btcPrice}
          lastUpdate={localStorage.getItem('lastUpdate')}
        />
        <Footer />
      </div>
    )
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
}

export default withStyles(styles)(App)



// WEBPACK FOOTER //
// ./src/App.js